/* eslint-disable camelcase */
import { ColorClubMatas1, Spacing16, Spacing8 } from 'autogen/design-tokens/tokens';
import {
    IBasketPrizeCertificateInfoBoxDiscriminator,
    type BasketPrizeCertificateModel,
    type BasketPrizeCertificateNotAvailableModel
} from 'autogen/swagger/all';
import { Typography } from 'components/generic/typography';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Heading, Saldo } from 'autogen/translation-keys/trans-website-basket-prizecertificate';
import ChoosablePrizeCertificatesRenderer from './components/ChoosablePrizeCertificatesRenderer';
import SummaryLine from '../BasketOverview/components/SummaryLines';
import Flex from 'components/Layout/components/Flex';

/* #region styles */
const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: ${Spacing8};
`;

const HeadingContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const PointText = styled.span`
    font-style: italic;
    color: ${ColorClubMatas1};
`;

const InformationContainer = styled.div`
    display: grid;
    gap: ${Spacing8};
`;

const Container = styled.div`
    display: grid;
    gap: ${Spacing16};
`;

const List = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
`;
/* #endregion */

type PrizeCertificateProps = {
    model: BasketPrizeCertificateModel | BasketPrizeCertificateNotAvailableModel;

    onCertificateSuccessfullyAdded?: () => void;
};

type BasketPrizeCertificateProps = {
    model: BasketPrizeCertificateModel;

    onCertificateSuccessfullyAdded?: () => void;
};

export default function PrizeCertificate({
    model,

    onCertificateSuccessfullyAdded
}: PrizeCertificateProps) {
    return IBasketPrizeCertificateInfoBoxDiscriminator(
        model,
        (basketPrizeCertificateModel) => (
            <BasketPrizeCerticateRenderer
                model={basketPrizeCertificateModel}
                onCertificateSuccessfullyAdded={onCertificateSuccessfullyAdded}
            />
        ),
        (basketPrizeCertificateNotAvailableModel) => (
            <BasketPrizeCerticateUnavailableRenderer model={basketPrizeCertificateNotAvailableModel} />
        ),
        () => null
    );
}

function BasketPrizeCerticateUnavailableRenderer({
    model: { reason }
}: { model: BasketPrizeCertificateNotAvailableModel }) {
    const { t } = useTranslation();

    return (
        <Container>
            <Flex.Column>
                <Typography.Title size="S">{t(Heading)}</Typography.Title>
                <Typography.Body as="span" size="M" strong>
                    {reason}
                </Typography.Body>
            </Flex.Column>
        </Container>
    );
}

function BasketPrizeCerticateRenderer({
    model,

    onCertificateSuccessfullyAdded
}: BasketPrizeCertificateProps) {
    const { t } = useTranslation();
    const { choosablePrizeCertificates, activePrizeCertificates, totalPriceCertificatesText, pointInfo, showSaldo } =
        model;

    const hasChoosablePrizeCertificates = !!choosablePrizeCertificates.length;
    const hasActivePrizeCertificates = !!activePrizeCertificates.length;

    const isAllPrizeCertificatesApplied = choosablePrizeCertificates.every(
        (choosablePrizeCertificate) => !choosablePrizeCertificate.isActive
    );

    if (!hasChoosablePrizeCertificates) return null;

    return (
        <Container>
            <InformationContainer>
                <HeadingContainer>
                    <Typography.Title size="S">{t(Heading)}</Typography.Title>
                    {showSaldo && (
                        <Typography.Body as="span" size="M" strong>
                            {t(Saldo)} <PointText>{pointInfo.remainingPoints} point</PointText>
                        </Typography.Body>
                    )}
                </HeadingContainer>

                {totalPriceCertificatesText && (
                    <Typography.Body as="span" size="M" strong>
                        {totalPriceCertificatesText}
                    </Typography.Body>
                )}
            </InformationContainer>

            {hasChoosablePrizeCertificates && !isAllPrizeCertificatesApplied && (
                <Grid>
                    <ChoosablePrizeCertificatesRenderer
                        model={choosablePrizeCertificates}
                        onCertificateSuccessfullyAdded={onCertificateSuccessfullyAdded}
                    />
                </Grid>
            )}

            {hasActivePrizeCertificates && (
                <List>
                    <SummaryLine.ActivePrizeCertificateRenderer
                        model={{ prizeCertificates: activePrizeCertificates }}
                    />
                </List>
            )}
        </Container>
    );
}
